import {mdiArrowBottomLeft, mdiEmailOpen} from '@mdi/js'

export const STYLE = {
  HEADER_HEIGHT: 30,
  SIDEBAR_WIDTH: 140,
  SIDEBAR_MARGIN_HEADER_TOP: 58
}

export const PATHS = {
  AGENDA: '/agenda',
  ANALYTICS: '/analytics',
  COMMUNICATIONS: '/communication-messages',
  CUSTOMER_USERS: '/customer-users',
  DIARY: '/diary',
  DOCUMENTS: '/documents',
  MY_DOCUMENTS: '/documents-and-materials',
  ECHOCARDIOGRAPHIC_REQUESTS: '/echocardiographic-requests',
  EXPORT: '/exports',
  INFO_MATERIALS: '/info-materials',
  MATERIAL_REQUESTS: '/material-requests',
  LOGIN: '/login',
  PASSWORD_RECOVERY: '/password-recovery',
  PATIENTS: '/patients',
  PATIENT: '/patients/:id',
  MEDICAL_CENTERS: '/medicalcenters',
  PATIENT_ACTIVITIES: '/patient-activities',
  ENROLLMENT_HUB: '/digital-enrollment',
  PATIENT_DIGITAL_ENROLLMENT: '/digital-enrollment/:id',
  PORTALS: '/portals',
  PROFILE: '/profile',
  QUESTIONNAIRE: '/questionnaire',
  SUMMARY: '/',
  TEST_DBS: '/biological-samples',
  THERAPIES: '/therapies',
  // Target of the link inside the assumption reminder (therapy-service ASSUMPTION_RESPONSE_PATH).
  // Deliberately NOT gated by sidebarItems and registered in BOTH routing stacks: the reminder
  // carries a single URL, but which stack renders it depends on the PSP's newStyleEnabled flag.
  SECONDARY_ASSUMPTION_RESPONSE: '/secondary-assumptions/:secondaryAssumptionId',
  // Opened by a click on the web push reminder: redirects to the dose it was sent for
  REMINDER: '/promemoria',
  VIRTUAL_MEETING_DETAIL: '/virtual-meeting-detail',
  VIRTUAL_MEETINGS: '/virtual-meetings',
  PDF_SUMMARY: 'pdf-summary',
  PDF_DIARY: 'pdf-diary',
  PERFORMANCES: '/performances',
  AUTO_REGISTRATION: '/self-registration',
  GRAPHS: '/graphs'
}

export const TABLE_ICONS: { [key: string]: string } = {
  mdiEmailOpen: mdiEmailOpen,
  mdiArrowBottomLeft: mdiArrowBottomLeft
}

export const STATUS = {
  PENDING: 'PENDING',
  FULFILLED: 'FULFILLED',
  REJECTED: 'REJECTED'
}

export const PREVIEW_MIME_TYPES = ['application/pdf', 'audio/mpeg', 'video/x-msvideo', 'video/mp4', 'video/mpeg', 'image/jpeg', 'image/png', 'image/svg+xml']

export const docTypesOptions = [
  {
    label: 'Documento PDF',
    value: 'DOCUMENT_PDF'
  },
  {
    label: 'Immagine',
    value: 'IMAGE'
  },
  {
    label: 'Video',
    value: 'VIDEO'
  },
  {
    label: 'Audio',
    value: 'AUDIO'
  },
  {
    label: 'Documento WORD',
    value: 'DOCUMENT_WORD'
  },
  {
    label: 'Documento Excel',
    value: 'DOCUMENT_EXCEL'
  },
  {
    label: 'Presentazione',
    value: 'PRESENTATION'
  },
  {
    label: 'Zip',
    value: 'ZIP'
  },
  {
    label: 'Altro',
    value: 'OTHER'
  }
]

// Ordine degli stati paziente mostrato all'utente (righe della lista e opzioni dei
// filtri): prima gli attivi, poi quelli in attivazione / da attivare, poi gli altri.
export const PATIENT_STATE_ORDER = ['ACTIVE', 'ACTIVE_DELIVERY', 'ACTIVATING', 'TO_ACTIVE', 'SUSPENDED', 'WAITING_TO_EXIT', 'EXITED']

// Rango di uno stato in PATIENT_STATE_ORDER; stati assenti (o mancanti) vanno in fondo.
export function getPatientStateRank(state?: string): number {
  const rank = state ? PATIENT_STATE_ORDER.indexOf(state) : -1
  return rank === -1 ? PATIENT_STATE_ORDER.length : rank
}

// Ordinamento di default della lista pazienti (new-style e legacy): prima per stato
// secondo PATIENT_STATE_ORDER (gli attivi in cima), poi per cognome e nome.
export function sortPatientsByStateAndName<T extends {state?: string; firstName?: string; lastName?: string}>(list: T[]): T[] {
  return [...list].sort((a, b) => {
    const rankA = getPatientStateRank(a.state)
    const rankB = getPatientStateRank(b.state)
    if (rankA !== rankB) return rankA - rankB
    const lastNameCompare = (a.lastName ?? '').localeCompare(b.lastName ?? '', 'it')
    if (lastNameCompare !== 0) return lastNameCompare
    return (a.firstName ?? '').localeCompare(b.firstName ?? '', 'it')
  })
}

// Riordina le opzioni di stato configurate dal PSP secondo PATIENT_STATE_ORDER,
// mantenendo l'ordine originale fra quelle non previste.
export function sortPatientStateOptions<T extends {value: string}>(options: T[]): T[] {
  return [...options].sort((a, b) => getPatientStateRank(a.value) - getPatientStateRank(b.value))
}

export const statesOptions = [
  {
    value: 'ACTIVE',
    label: 'Attivo'
  },
  {
    value: 'ACTIVE_DELIVERY',
    label: 'Attivo delivery'
  },
  {
    value: 'ACTIVATING',
    label: 'in attivazione'
  },
  {
    value: 'TO_ACTIVE',
    label: 'Da attivare'
  },
  {
    value: 'SUSPENDED',
    label: 'Sospeso'
  },
  {
    value: 'WAITING_TO_EXIT',
    label: 'In attesa di uscita dal psp'
  },
  {
    value: 'EXITED',
    label: 'Uscito'
  }
]

export const THERAPY_DOSAGE_UNIT_TYPE_OPTIONS = [
  {value: 'mg', label: 'mg'}
  // {value: 'ml', label: 'ml'}
  // {value: 'UI', label: 'UI'},
  // {value: 'Fiale', label: 'Fiale'},
  // {value: 'Pillole', label: 'Pillole'},
  // {value: 'Compresse', label: 'Compresse'},
  // {value: 'mg/ml', label: 'mg/ml'},
  // {value: 'mg/Km-WEIGHT', label: 'mg/Km peso'},
  // {value: 'ng/Km-WEIGHT', label: 'ng/Km peso'}
]

export const THERAPY_DAILY_DOSAGE_UNIT_TYPE_OPTIONS = [
  {value: 'ml', label: 'ml'}
  // {value: 'mg', label: 'mg'},
  // {value: 'UI', label: 'UI'},
  // {value: 'Fiale', label: 'Fiale'},
  // {value: 'Pillole', label: 'Pillole'},
  // {value: 'Compresse', label: 'Compresse'},
  // {value: 'mg/ml', label: 'mg/ml'},
  // {value: 'mg/Km-WEIGHT', label: 'mg/Km peso'},
  // {value: 'ng/Km-WEIGHT', label: 'ng/Km peso'}
]

export const THERAPY_SPEED_OF_DEVICE_UNIT_OPTIONS = [
  {value: 'ml/h', label: 'ml/h'}
  // {value: 'ng/kg/min', label: 'ng/kg/min'}
  // {value: '', label: ''},
  // {value: 'ml/min', label: 'ml/min'}
]

export const THERAPY_SPEED_OF_ASSUMPTION_UNIT_OPTIONS = [
  {value: 'ng/kg/min', label: 'ng/kg/min'}
  // {value: 'ml/h', label: 'ml/h'},
  // {value: 'ml/min', label: 'ml/min'}
]

// Dominio di caregiverType usato ovunque nell'app per il campo caregiver/delegato
// (family card del profilo E caregivers/tutori del paziente: stesso field, stessi
// valori — vedi FamilyCardSection.tsx, ProfileNew.tsx e il fixture mock
// psp-backend/patients/{patientId}/caregivers/get.js).
// Non è configurato da PSP config/DB: è hardcoded, allineato al dominio "legale"
// completo usato lato portale interno (legalTypes/caregiverTypes in
// insurance-user-page-web-component/.../associatedUser.js e
// patient-page-web-component/.../caregivers.js). Nota: i fixture mock di questo
// repo restituiscono anche SON/SPOUSE/OTHER, che con questo dominio non hanno una
// label e vanno in fallback sul valore grezzo.
export const familyTypes = {
  PARENT: 'Genitore',
  CUSTODIAL: 'Affidatario',
  SUPPORT_ADMINISTRATOR: 'Amministratore di sostegno',
  SPECIAL_PROSECUTOR: 'Procuratore speciale',
  LEGAL_TUTOR: 'Tutore legale'
}

export const beneficiaryOptions = [
  {value: 'beneficiary', label: 'Titolare'},
  {value: 'parent', label: 'Genitore'},
  {value: 'spouse', label: 'Coniuge'},
  {value: 'son', label: 'Figlio/a'},
  {value: 'other', label: 'Altro'},
  {value: 'twoElementCore', label: 'Nucleo di 2 elementi'},
  {value: 'threeElementCore', label: 'Nucleo di 3 elementi'},
  {value: 'fourElementCore', label: 'Nucleo di 4 elementi'},
  {value: 'fivePlusElementCore', label: 'Nucleo di 5+ elementi'},
  {value: 'product', label: 'Prodotto'}
]

export const BODY_PARTS = ['Testa', 'Collo', 'Spalla', 'Braccio', 'Gomito', 'Avambraccio', 'Polso', 'Mano',
  'Schiena', 'Torace', 'Addome', 'Fianco', 'Gluteo', 'Gamba', 'Ginocchio', 'Caviglia', 'Piede']

export const BODY_PARTS_OPTIONS = BODY_PARTS.map(part => ({label: part, value: part}))
